import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Send,
  Email,
  WhatsApp,
  Person,
  Group,
  CheckCircle,
  PendingActions,
  ExpandMore,
  Preview,
} from '@mui/icons-material';
import { Booking } from '@/types';
import { useBulkCommunication, BulkCommunicationRequest } from '@/hooks/api/useGuests';

interface BulkCommunicationDialogProps {
  open: boolean;
  onClose: () => void;
  booking: Booking;
  listingTitle?: string;
  prefilledMessage?: string;
  prefilledSubject?: string;
}

interface MessagePreview {
  channel: 'email' | 'whatsapp';
  subject?: string;
  message: string;
  recipientCount: number;
}

export const BulkCommunicationDialog: React.FC<BulkCommunicationDialogProps> = ({
  open,
  onClose,
  booking,
  listingTitle,
  prefilledMessage = '',
  prefilledSubject = '',
}) => {
  const [formData, setFormData] = useState({
    channel: 'both' as 'email' | 'whatsapp' | 'both',
    subject: prefilledSubject,
    message: prefilledMessage,
    includeCompleted: false,
    sendToHost: false,
  });
  const [showPreview, setShowPreview] = useState(false);

  const bulkCommunicationMutation = useBulkCommunication();

  // Get guest status for recipient calculation
  const guestStatus = booking.guestCheckInStatus || { completed: 0, pending: 0 };
  const totalGuests = booking.expectedGuests || booking.guests || 0;
  
  // Calculate actual pending count: total guests minus completed
  const actualPending = Math.max(0, totalGuests - guestStatus.completed);
  const actualCompleted = guestStatus.completed;
  const recipientCount = formData.includeCompleted ? totalGuests : actualPending;

  const handleSubmit = async () => {
    if (!formData.message.trim()) return;

    try {
      const request: BulkCommunicationRequest = {
        bookingId: booking.bookingId,
        channel: formData.channel,
        subject: formData.subject || undefined,
        message: formData.message,
        includeCompleted: formData.includeCompleted,
      };

      await bulkCommunicationMutation.mutateAsync(request);
      onClose();
      
      // Reset form
      setFormData({
        channel: 'both',
        subject: '',
        message: '',
        includeCompleted: false,
        sendToHost: false,
      });
    } catch (error) {
      console.error('Failed to send bulk communication:', error);
    }
  };

  const handleClose = () => {
    onClose();
    setShowPreview(false);
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return <Email fontSize="small" />;
      case 'whatsapp':
        return <WhatsApp fontSize="small" />;
      default:
        return <Send fontSize="small" />;
    }
  };

  const getPreviewMessages = (): MessagePreview[] => {
    const previews: MessagePreview[] = [];
    
    if (formData.channel === 'email' || formData.channel === 'both') {
      previews.push({
        channel: 'email',
        subject: formData.subject || 'Message from your host',
        message: formData.message,
        recipientCount,
      });
    }
    
    if (formData.channel === 'whatsapp' || formData.channel === 'both') {
      previews.push({
        channel: 'whatsapp',
        message: formData.message,
        recipientCount,
      });
    }
    
    return previews;
  };

  const substituteVariables = (text: string) => {
    // Replace common variables with example values for preview
    return text
      .replace(/\{\{guestName\}\}/g, 'John Smith')
      .replace(/\{\{hostName\}\}/g, 'Sarah Johnson')
      .replace(/\{\{propertyName\}\}/g, listingTitle || 'Your Property')
      .replace(/\{\{checkInDate\}\}/g, new Date(booking.startDate).toLocaleDateString())
      .replace(/\{\{checkOutDate\}\}/g, new Date(booking.endDate).toLocaleDateString())
      .replace(/\{\{bookingId\}\}/g, booking.bookingId.slice(-8));
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Group />
          <Typography variant="h6">
            Send Bulk Communication
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {/* Booking Information */}
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="subtitle1" gutterBottom>
              Booking Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Booking ID
                </Typography>
                <Typography variant="body1">
                  #{booking.bookingId.slice(-8)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Property
                </Typography>
                <Typography variant="body1">
                  {listingTitle || 'Unknown Property'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Check-in Date
                </Typography>
                <Typography variant="body1">
                  {new Date(booking.startDate).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Total Guests
                </Typography>
                <Typography variant="body1">
                  {totalGuests}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Recipients */}
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="subtitle1" gutterBottom>
              Recipients
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  label={`${actualPending} pending check-in`}
                  color="warning"
                  icon={<PendingActions fontSize="small" />}
                />
                <Chip
                  label={`${actualCompleted} completed check-in`}
                  color="success"
                  icon={<CheckCircle fontSize="small" />}
                />
              </Box>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.includeCompleted}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      includeCompleted: e.target.checked 
                    }))}
                  />
                }
                label="Include guests who have already completed check-in"
              />
              
              <Alert severity="info">
                This message will be sent to {recipientCount} guest{recipientCount !== 1 ? 's' : ''}
              </Alert>
            </Box>
          </Paper>

          {/* Message Configuration */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Communication Channel</InputLabel>
              <Select
                value={formData.channel}
                label="Communication Channel"
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  channel: e.target.value as any 
                }))}
              >
                <MenuItem value="both">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email fontSize="small" />
                    <WhatsApp fontSize="small" />
                    Email + WhatsApp
                  </Box>
                </MenuItem>
                <MenuItem value="email">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email fontSize="small" />
                    Email Only
                  </Box>
                </MenuItem>
                <MenuItem value="whatsapp">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WhatsApp fontSize="small" />
                    WhatsApp Only
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {(formData.channel === 'email' || formData.channel === 'both') && (
              <TextField
                label="Email Subject"
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  subject: e.target.value 
                }))}
                fullWidth
                placeholder="Important information about your stay"
              />
            )}

            <TextField
              label="Message"
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                message: e.target.value 
              }))}
              multiline
              rows={6}
              fullWidth
              required
              placeholder="Hello! We wanted to share some important information about your upcoming stay..."
              helperText="You can use variables like {{guestName}}, {{propertyName}}, {{checkInDate}}, etc."
            />
          </Box>

          {/* Message Preview */}
          {formData.message.trim() && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Preview />
                  <Typography variant="subtitle1">
                    Message Preview
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {getPreviewMessages().map((preview, index) => (
                    <Paper key={index} sx={{ p: 2 }} variant="outlined">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        {getChannelIcon(preview.channel)}
                        <Typography variant="subtitle2">
                          {preview.channel.charAt(0).toUpperCase() + preview.channel.slice(1)} Preview
                        </Typography>
                        <Chip
                          label={`${preview.recipientCount} recipients`}
                          size="small"
                          color="primary"
                        />
                      </Box>
                      
                      {preview.subject && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Subject:
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {substituteVariables(preview.subject)}
                          </Typography>
                        </Box>
                      )}
                      
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Message:
                        </Typography>
                        <Paper 
                          sx={{ 
                            p: 2, 
                            mt: 1,
                            bgcolor: preview.channel === 'whatsapp' ? 'green.50' : 'grey.50' 
                          }}
                        >
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {substituteVariables(preview.message)}
                          </Typography>
                        </Paper>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Available Variables */}
          <Paper sx={{ p: 2, bgcolor: 'blue.50' }} variant="outlined">
            <Typography variant="subtitle2" gutterBottom>
              Available Variables
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[
                'guestName', 'hostName', 'propertyName', 'checkInDate', 
                'checkOutDate', 'bookingId', 'totalGuests'
              ].map((variable) => (
                <Chip
                  key={variable}
                  label={`{{${variable}}}`}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const newMessage = formData.message + `{{${variable}}}`;
                    setFormData(prev => ({ ...prev, message: newMessage }));
                  }}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Click on a variable to add it to your message
            </Typography>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.message.trim() || bulkCommunicationMutation.isLoading || recipientCount === 0}
          startIcon={<Send />}
        >
          {bulkCommunicationMutation.isLoading 
            ? 'Sending...' 
            : `Send to ${recipientCount} Guest${recipientCount !== 1 ? 's' : ''}`
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkCommunicationDialog;